import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './StudentActions.css';

function Parents() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [parents, setParents] = useState([]);
  const [loadingParents, setLoadingParents] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedParentAllParents, setSelectedParentAllParents] = useState([]); // All parents for the student(s)
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [emergencyContactInfo, setEmergencyContactInfo] = useState({ contact: null, phone: null });
  const { selectedBranchId, handleBranchChange, refreshTrigger } = useBranchFilter();
  const [editingParent, setEditingParent] = useState(null);
  const [editingParentAllParents, setEditingParentAllParents] = useState([]);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editForm, setEditForm] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    parentRelationship: '',
    parentAddress: '',
    parentIsPrimary: false,
    parentCanPickup: false,
    parentIsEmergencyContact: false,
    parentPaysFees: false
  });
  const [showArchiveConfirmPopup, setShowArchiveConfirmPopup] = useState(false);
  const [parentToArchive, setParentToArchive] = useState(null);
  const [showArchiveSuccessPopup, setShowArchiveSuccessPopup] = useState(false);
  const [archivedParentName, setArchivedParentName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
  const [showRestoreConfirmPopup, setShowRestoreConfirmPopup] = useState(false);
  const [parentToRestore, setParentToRestore] = useState(null);
  const [showRestoreSuccessPopup, setShowRestoreSuccessPopup] = useState(false);
  const [restoredParentName, setRestoredParentName] = useState('');
  const [showUpdateSuccessPopup, setShowUpdateSuccessPopup] = useState(false);
  const [updatedParentName, setUpdatedParentName] = useState('');
  const [parentDocuments, setParentDocuments] = useState({}); // Store documents by parent ID

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Fetch parents when schoolData is available
    if (schoolData?.id) {
      fetchParents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolData, selectedBranchId, refreshTrigger, viewMode]);

  // Listen for branch change events - force refresh when branch changes
  useEffect(() => {
    const handleBranchChange = (event) => {
      // Get branchId from event detail
      const branchId = event?.detail?.branchId !== undefined ? event.detail.branchId : null;
      
      // Small delay to ensure state has updated, then fetch with the new branchId
      setTimeout(() => {
        if (schoolData?.id) {
          fetchParents(branchId);
        }
      }, 50);
    };

    window.addEventListener('branchChanged', handleBranchChange);
    return () => {
      window.removeEventListener('branchChanged', handleBranchChange);
    };
  }, [schoolData?.id]);

  const fetchParents = async (branchIdOverride = null) => {
    if (!schoolData?.id) return;
    
    // Get the current branchId - use override, then hook value, then localStorage
    const currentBranchId = branchIdOverride !== null 
      ? branchIdOverride 
      : (selectedBranchId !== undefined 
          ? selectedBranchId 
          : (() => {
              const saved = localStorage.getItem('selectedBranchId');
              return saved && saved !== 'null' ? parseInt(saved) : null;
            })());
    
    try {
      setLoadingParents(true);
      
      // Fetch all students to extract unique parents
      let studentsQuery = supabase
        .from('students')
        .select('parent_name, parent_email, parent_phone, parent_relationship, parent_address, address, parent_is_primary, parent_can_pickup, parent_is_emergency_contact, parent_pays_fees')
        .eq('school_id', schoolData.id);
      
      // Filter by active/inactive status based on view mode
      if (viewMode === 'active') {
        studentsQuery = studentsQuery.eq('is_active', true);
      } else {
        studentsQuery = studentsQuery.eq('is_active', false);
      }
      
      // Filter by branch if one is selected
      if (currentBranchId) {
        studentsQuery = studentsQuery.eq('branch_id', currentBranchId);
      }
      
      const { data: studentsData, error: studentsError } = await studentsQuery.order('parent_name');
      
      if (studentsError) {
        console.error('Error fetching students for parents:', studentsError);
        setError('Failed to load parents');
        return;
      }
      
      // Group by parent_email to get unique parents
      const parentsMap = new Map();
      
      studentsData.forEach(student => {
        if (student.parent_email) {
          const email = student.parent_email.toLowerCase().trim();
          
          if (!parentsMap.has(email)) {
            parentsMap.set(email, {
              parent_name: student.parent_name || '',
              parent_email: student.parent_email || '',
              parent_phone: student.parent_phone || '',
              parent_relationship: student.parent_relationship || '',
              parent_address: student.parent_address || student.address || '',
              parent_is_primary: student.parent_is_primary || false,
              parent_can_pickup: student.parent_can_pickup || false,
              parent_is_emergency_contact: student.parent_is_emergency_contact || false,
              parent_pays_fees: student.parent_pays_fees || false,
              children_count: 0,
              children: []
            });
          }
          
          // Count children and store child info
          const parent = parentsMap.get(email);
          parent.children_count += 1;
          parent.children.push({
            name: `${student.parent_name || ''}`.split(' ')[0] || 'Student'
          });
        }
      });
      
      // Fetch children details for each parent
      for (const [email, parent] of parentsMap.entries()) {
        let childrenQuery = supabase
          .from('students')
          .select('id, first_name, middle_name, last_name, class_id, section_id, classes(class_name)')
          .eq('school_id', schoolData.id)
          .eq('parent_email', parent.parent_email);
        
        // Filter by active/inactive status based on view mode
        if (viewMode === 'active') {
          childrenQuery = childrenQuery.eq('is_active', true);
        } else {
          childrenQuery = childrenQuery.eq('is_active', false);
        }
        
        // Filter by branch if one is selected
        if (currentBranchId) {
          childrenQuery = childrenQuery.eq('branch_id', currentBranchId);
        }
        
        const { data: childrenData } = await childrenQuery.order('first_name');
        
        if (childrenData) {
          parent.children = childrenData.map(child => ({
            id: child.id,
            name: `${child.first_name || ''} ${child.middle_name || ''} ${child.last_name || ''}`.trim(),
            class: child.classes?.class_name || child.class_id || 'N/A',
            section: child.section_id || 'N/A'
          }));
        }
      }
      
      // Convert map to array and sort by name
      const parentsArray = Array.from(parentsMap.values()).sort((a, b) => {
        return (a.parent_name || '').localeCompare(b.parent_name || '');
      });
      
      setParents(parentsArray);
      setError(null);
    } catch (err) {
      console.error('Exception fetching parents:', err);
      setError('Failed to load parents');
    } finally {
      setLoadingParents(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    }
  };

  // Filter parents based on search term
  const filteredParents = parents.filter(parent => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (parent.parent_name || '').toLowerCase().includes(searchLower) ||
      (parent.parent_email || '').toLowerCase().includes(searchLower) ||
      (parent.parent_phone || '').includes(searchTerm)
    );
  });

  const handleShowDetails = async (parent) => {
    setSelectedParent(parent);
    
    // Fetch all students associated with this parent email
    let studentsQuery = supabase
      .from('students')
      .select('id, emergency_contact, emergency_phone')
      .eq('school_id', schoolData.id)
      .eq('parent_email', parent.parent_email);
    
    if (viewMode === 'active') {
      studentsQuery = studentsQuery.eq('is_active', true);
    } else {
      studentsQuery = studentsQuery.eq('is_active', false);
    }
    
    const { data: studentsData } = await studentsQuery;
    
    // Get emergency contact info from first student (assuming all children have same emergency contact)
    const emergencyContact = studentsData && studentsData.length > 0 ? studentsData[0].emergency_contact : null;
    const emergencyPhone = studentsData && studentsData.length > 0 ? studentsData[0].emergency_phone : null;
    
    // Fetch all parents for these students from parents table
    let allParents = [];
    if (studentsData && studentsData.length > 0) {
      const studentIds = studentsData.map(s => s.id);
      
      // Get all parent relationships for these students
      const { data: studentParents } = await supabase
        .from('student_parents')
        .select(`
          *,
          parents:parent_id (
            id,
            name,
            email,
            phone,
            relationship,
            address,
            can_pickup,
            is_emergency_contact,
            pays_fees,
            is_primary
          )
        `)
        .in('student_id', studentIds)
        .order('is_primary', { ascending: false });
      
      if (studentParents && studentParents.length > 0) {
        allParents = studentParents
          .map(sp => ({
            ...sp.parents,
            is_primary_for_student: sp.is_primary,
            student_id: sp.student_id
          }))
          .filter(p => p !== null && p.id);
      }
      
      // If no parents found in parents table, fall back to student's parent fields
      if (allParents.length === 0) {
        allParents = [{
          id: null,
          name: parent.parent_name || '',
          email: parent.parent_email || '',
          phone: parent.parent_phone || '',
          relationship: parent.parent_relationship || '',
          address: parent.parent_address || '',
          can_pickup: parent.parent_can_pickup || false,
          is_emergency_contact: parent.parent_is_emergency_contact || false,
          pays_fees: parent.parent_pays_fees || false,
          is_primary_for_student: parent.parent_is_primary || false
        }];
      }
    } else {
      // Fallback to single parent if no students found
      allParents = [{
        id: null,
        name: parent.parent_name || '',
        email: parent.parent_email || '',
        phone: parent.parent_phone || '',
        relationship: parent.parent_relationship || '',
        address: parent.parent_address || '',
        can_pickup: parent.parent_can_pickup || false,
        is_emergency_contact: parent.parent_is_emergency_contact || false,
        pays_fees: parent.parent_pays_fees || false,
        is_primary_for_student: parent.parent_is_primary || false
      }];
    }
    
    setSelectedParentAllParents(allParents);
    
    // Fetch parent documents for all parents
    const documentsMap = {};
    for (const parent of allParents) {
      if (parent.id) {
        try {
          const { data: docs, error: docsError } = await supabase
            .from('student_documents')
            .select('*')
            .eq('parent_id', parent.id)
            .in('document_type', ['Parent National ID', 'Parent Passport'])
            .order('uploaded_at', { ascending: false });
          
          if (!docsError && docs) {
            documentsMap[parent.id] = docs;
          }
        } catch (err) {
          console.error(`Error fetching documents for parent ${parent.id}:`, err);
        }
      }
    }
    setParentDocuments(documentsMap);
    
    setShowDetailsPopup(true);
  };

  const handleCloseDetails = () => {
    setSelectedParent(null);
    setSelectedParentAllParents([]);
    setEmergencyContactInfo({ contact: null, phone: null });
    setParentDocuments({});
    setShowDetailsPopup(false);
  };

  const handleEditParent = async (parent) => {
    setEditingParent(parent);
    
    // Fetch all students associated with this parent email
    let studentsQuery = supabase
      .from('students')
      .select('id, emergency_contact, emergency_phone')
      .eq('school_id', schoolData.id)
      .eq('parent_email', parent.parent_email);
    
    if (viewMode === 'active') {
      studentsQuery = studentsQuery.eq('is_active', true);
    } else {
      studentsQuery = studentsQuery.eq('is_active', false);
    }
    
    const { data: studentsData } = await studentsQuery;
    
    // Get emergency contact info from first student (assuming all children have same emergency contact)
    const emergencyContact = studentsData && studentsData.length > 0 ? studentsData[0].emergency_contact : null;
    const emergencyPhone = studentsData && studentsData.length > 0 ? studentsData[0].emergency_phone : null;
    
    // Store emergency contact info
    setEmergencyContactInfo({
      contact: emergencyContact,
      phone: emergencyPhone
    });
    
    // Fetch all parents for these students from parents table
    let allParents = [];
    if (studentsData && studentsData.length > 0) {
      const studentIds = studentsData.map(s => s.id);
      
      // Get all parent relationships for these students
      const { data: studentParents } = await supabase
        .from('student_parents')
        .select(`
          *,
          parents:parent_id (
            id,
            name,
            email,
            phone,
            relationship,
            address,
            can_pickup,
            is_emergency_contact,
            pays_fees,
            is_primary
          )
        `)
        .in('student_id', studentIds)
        .order('is_primary', { ascending: false });
      
      if (studentParents && studentParents.length > 0) {
        allParents = studentParents
          .map(sp => ({
            ...sp.parents,
            is_primary_for_student: sp.is_primary,
            student_id: sp.student_id
          }))
          .filter(p => p !== null && p.id);
      }
      
      // If no parents found in parents table, fall back to student's parent fields
      if (allParents.length === 0) {
        allParents = [{
          id: null,
          name: parent.parent_name || '',
          email: parent.parent_email || '',
          phone: parent.parent_phone || '',
          relationship: parent.parent_relationship || '',
          address: parent.parent_address || '',
          can_pickup: parent.parent_can_pickup || false,
          is_emergency_contact: parent.parent_is_emergency_contact || false,
          pays_fees: parent.parent_pays_fees || false,
          is_primary_for_student: parent.parent_is_primary || false
        }];
      }
    } else {
      // Fallback to single parent if no students found
      allParents = [{
        id: null,
        name: parent.parent_name || '',
        email: parent.parent_email || '',
        phone: parent.parent_phone || '',
        relationship: parent.parent_relationship || '',
        address: parent.parent_address || '',
        can_pickup: parent.parent_can_pickup || false,
        is_emergency_contact: parent.parent_is_emergency_contact || false,
        pays_fees: parent.parent_pays_fees || false,
        is_primary_for_student: parent.parent_is_primary || false
      }];
    }
    
    setEditingParentAllParents(allParents);
    
    // Set edit form with first parent (or the clicked parent)
    const firstParent = allParents[0] || parent;
    setEditForm({
      parentName: firstParent.name || firstParent.parent_name || '',
      parentEmail: firstParent.email || firstParent.parent_email || '',
      parentPhone: firstParent.phone || firstParent.parent_phone || '',
      parentRelationship: firstParent.relationship || firstParent.parent_relationship || '',
      parentAddress: firstParent.address || firstParent.parent_address || '',
      parentIsPrimary: firstParent.is_primary_for_student || firstParent.parent_is_primary || false,
      parentCanPickup: firstParent.can_pickup || firstParent.parent_can_pickup || false,
      parentIsEmergencyContact: firstParent.is_emergency_contact || firstParent.parent_is_emergency_contact || false,
      parentPaysFees: firstParent.pays_fees || firstParent.parent_pays_fees || false
    });
    setShowEditPopup(true);
  };

  // Helper function to get parent label based on relationship
  const getParentLabel = (parent, parentIndex, allParents) => {
    const relationship = (parent.relationship || parent.parent_relationship || '').trim();
    
    if (relationship === 'Mother') {
      return 'Mother';
    } else if (relationship === 'Father') {
      return 'Father';
    } else if (relationship === 'Guardian' || relationship.toLowerCase().includes('guardian')) {
      // Count guardians up to and including this parent
      let guardianNumber = 0;
      for (let i = 0; i <= parentIndex; i++) {
        const p = allParents[i];
        const rel = ((p.relationship || p.parent_relationship) || '').trim().toLowerCase();
        if (rel === 'guardian' || rel.includes('guardian')) {
          guardianNumber++;
        }
      }
      return `Guardian ${guardianNumber}`;
    } else {
      // Fallback: if no relationship set, use Parent X
      return `Parent ${parentIndex + 1}`;
    }
  };

  const handleCloseEditPopup = () => {
    setShowEditPopup(false);
    setEditingParent(null);
    setEditingParentAllParents([]);
    setEditForm({
      parentName: '',
      parentEmail: '',
      parentPhone: '',
      parentRelationship: '',
      parentAddress: '',
      parentIsPrimary: false,
      parentCanPickup: false,
      parentIsEmergencyContact: false,
      parentPaysFees: false
    });
  };

  const handleSaveParent = async () => {
    if (!editingParent) return;

    // Validate required fields
    if (!editForm.parentName || !editForm.parentEmail) {
      alert('Please fill in required fields (Name and Email).');
      return;
    }

    setIsSubmitting(true);
    try {
      // Update all students associated with this parent email
      const oldEmail = editingParent.parent_email;
      const newEmail = editForm.parentEmail.trim().toLowerCase();

      // Update all students with the old parent email
      const updateData = {
        parent_name: editForm.parentName,
        parent_email: newEmail,
        parent_phone: editForm.parentPhone || null,
        parent_relationship: editForm.parentRelationship || null,
        parent_address: editForm.parentAddress || null,
        parent_is_primary: editForm.parentIsPrimary,
        parent_can_pickup: editForm.parentCanPickup,
        parent_is_emergency_contact: editForm.parentIsEmergencyContact,
        parent_pays_fees: editForm.parentPaysFees,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('students')
        .update(updateData)
        .eq('school_id', schoolData.id)
        .eq('parent_email', oldEmail);

      if (error) {
        console.error('Error updating parent:', error);
        alert('Failed to update parent. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Store updated parent name for success popup
      const updatedName = editForm.parentName;
      
      // Refresh parents list
      await fetchParents();
      handleCloseEditPopup();
      
      // Show success popup
      setUpdatedParentName(updatedName);
      setShowUpdateSuccessPopup(true);
    } catch (err) {
      console.error('Error updating parent:', err);
      alert('Failed to update parent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveParent = (parent) => {
    setParentToArchive(parent);
    setShowArchiveConfirmPopup(true);
  };

  const confirmArchiveParent = async () => {
    if (!parentToArchive) return;

    setIsSubmitting(true);
    try {
      // Archive all students associated with this parent email
      const { error } = await supabase
        .from('students')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('school_id', schoolData.id)
        .eq('parent_email', parentToArchive.parent_email);

      if (error) {
        console.error('Error archiving parent students:', error);
        alert('Failed to archive parent. Please try again.');
        setIsSubmitting(false);
        setShowArchiveConfirmPopup(false);
        setParentToArchive(null);
        return;
      }

      // Store archived parent name for success popup
      const archivedName = parentToArchive.parent_name;
      
      // Refresh parents list
      await fetchParents();
      setShowArchiveConfirmPopup(false);
      setParentToArchive(null);
      
      // Show success popup
      setArchivedParentName(archivedName);
      setShowArchiveSuccessPopup(true);
    } catch (err) {
      console.error('Error archiving parent:', err);
      alert('Failed to archive parent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelArchiveParent = () => {
    setShowArchiveConfirmPopup(false);
    setParentToArchive(null);
  };

  const handleRestoreParent = (parent) => {
    setParentToRestore(parent);
    setShowRestoreConfirmPopup(true);
  };

  const confirmRestoreParent = async () => {
    if (!parentToRestore) return;

    setIsSubmitting(true);
    try {
      // Restore all students associated with this parent email
      const { error } = await supabase
        .from('students')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('school_id', schoolData.id)
        .eq('parent_email', parentToRestore.parent_email);

      if (error) {
        console.error('Error restoring parent students:', error);
        alert('Failed to restore parent. Please try again.');
        setIsSubmitting(false);
        setShowRestoreConfirmPopup(false);
        setParentToRestore(null);
        return;
      }

      // Store restored parent name for success popup
      const restoredName = parentToRestore.parent_name;
      
      // Refresh parents list
      await fetchParents();
      setShowRestoreConfirmPopup(false);
      setParentToRestore(null);
      
      // Show success popup
      setRestoredParentName(restoredName);
      setShowRestoreSuccessPopup(true);
    } catch (err) {
      console.error('Error restoring parent:', err);
      alert('Failed to restore parent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRestoreParent = () => {
    setShowRestoreConfirmPopup(false);
    setParentToRestore(null);
  };

  // Export parents data to Excel (CSV format)
  const handleExportData = () => {
    if (parents.length === 0) {
      alert('No parents data to export.');
      return;
    }

    try {
      // Prepare data for export
      const exportData = parents.map((parent, index) => {
        // Format children list
        const childrenList = parent.children && parent.children.length > 0
          ? parent.children.map(child => `${child.name} (${child.class || 'N/A'})`).join('; ')
          : 'N/A';
        
        return {
          'S.No': index + 1,
          'Parent Name': parent.parent_name || 'N/A',
          'Email': parent.parent_email || 'N/A',
          'Phone': parent.parent_phone || 'N/A',
          'Relationship': parent.parent_relationship || 'N/A',
          'Address': parent.parent_address || 'N/A',
          'Primary Contact': parent.parent_is_primary ? 'Yes' : 'No',
          'Can Pickup': parent.parent_can_pickup ? 'Yes' : 'No',
          'Emergency Contact': parent.parent_is_emergency_contact ? 'Yes' : 'No',
          'Pays Fees': parent.parent_pays_fees ? 'Yes' : 'No',
          'Number of Children': parent.children_count || 0,
          'Children': childrenList
        };
      });

      // Convert to CSV format
      const headers = Object.keys(exportData[0]);
      const csvRows = [];
      
      // Add headers
      csvRows.push(headers.join(','));
      
      // Add data rows
      exportData.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values.join(','));
      });
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8 support
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0];
      const filename = `parents_export_${dateStr}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported ${parents.length} parents to ${filename}`);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="students-content">
          <div className="students-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1>Parents Management</h1>
                <p>{viewMode === 'active' ? 'View and manage all active parents in your school' : 'View and manage archived parents in your school'}</p>
              </div>
            </div>
          </div>

          <div className="students-stats">
            <div className="stat-item">
              <span className="stat-number">{parents.length}</span>
              <span className="stat-label">Total Parents</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {parents.filter(parent => parent.parent_relationship === 'Father').length}
              </span>
              <span className="stat-label">Fathers</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {parents.filter(parent => parent.parent_relationship === 'Mother').length}
              </span>
              <span className="stat-label">Mothers</span>
            </div>
          </div>

          <div className="students-actions" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <button 
              className="export-data-btn"
              onClick={handleExportData}
              disabled={parents.length === 0}
              style={{
                padding: '0.875rem 1.5rem',
                background: parents.length === 0 ? '#D1D5DB' : '#10B981',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: parents.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: parents.length === 0 ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                if (parents.length > 0) {
                  e.currentTarget.style.background = '#059669';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (parents.length > 0) {
                  e.currentTarget.style.background = '#10B981';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                }
              }}
            >
              📊 Export Data
            </button>
          </div>

          {/* View Mode Toggle */}
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', background: '#F3F4F6', borderRadius: '8px', padding: '0.25rem', width: 'fit-content', marginBottom: '1.5rem' }}>
            <button
              onClick={() => setViewMode('active')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: viewMode === 'active' ? '#FFFFFF' : 'transparent',
                color: viewMode === 'active' ? '#1F2937' : '#6B7280',
                fontWeight: viewMode === 'active' ? '600' : '400',
                cursor: 'pointer',
                boxShadow: viewMode === 'active' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              👥 Active Parents
            </button>
            <button
              onClick={() => setViewMode('archived')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: viewMode === 'archived' ? '#FFFFFF' : 'transparent',
                color: viewMode === 'archived' ? '#1F2937' : '#6B7280',
                fontWeight: viewMode === 'archived' ? '600' : '400',
                cursor: 'pointer',
                boxShadow: viewMode === 'archived' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              📦 Archived Parents
            </button>
          </div>

          {/* Search Bar */}
          <div className="students-filter-section" style={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#1F2937', fontSize: '1.1rem', fontWeight: '600' }}>
              🔍 Search Parents
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                  Search (Name/Email/Phone)
                </label>
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {/* Parents Table */}
          {loadingParents ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading parents...</p>
            </div>
          ) : filteredParents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👨‍👩‍👧‍👦</div>
              <h3>No {viewMode === 'active' ? 'Active' : 'Archived'} Parents Found</h3>
              <p>
                {searchTerm 
                  ? `No ${viewMode === 'active' ? 'active' : 'archived'} parents found matching your search.`
                  : viewMode === 'active' 
                    ? "You haven't added any active parents yet."
                    : "You don't have any archived parents yet."}
              </p>
            </div>
          ) : (
            <div className="students-table-container">
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Parent Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Relationship</th>
                    <th>Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParents.map((parent, index) => (
                    <tr key={parent.parent_email || index}>
                      <td className="student-name">
                        {parent.parent_name || 'N/A'}
                      </td>
                      <td className="email-cell">
                        {parent.parent_email ? (
                          <a href={`mailto:${parent.parent_email}`} className="email-link">
                            {parent.parent_email}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td className="email-cell">
                        {parent.parent_phone ? (
                          <a 
                            href={`tel:${parent.parent_phone}`} 
                            className="email-link"
                          >
                            {parent.parent_phone}
                          </a>
                        ) : 'N/A'}
                      </td>
                      <td className="class-cell">
                        <span className="class-name">
                          {parent.parent_relationship || 'N/A'}
                        </span>
                      </td>
                      <td className="parent-cell" style={{ maxWidth: '350px', minWidth: '200px', wordWrap: 'break-word', padding: '0.75rem' }}>
                        <span style={{ display: 'block', color: '#1F2937', fontSize: '0.9rem', lineHeight: '1.4' }}>
                          {parent.parent_address || 'N/A'}
                        </span>
                      </td>
                      <td className="actions-cell students-actions-container">
                        <div className="action-buttons students-action-buttons">
                          <button 
                            className="students-action-btn view-btn"
                            title="View Parent Details"
                            onClick={() => handleShowDetails(parent)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            <span>View</span>
                          </button>
                          <button 
                            className="students-action-btn edit-btn"
                            title="Edit Parent"
                            onClick={() => handleEditParent(parent)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                            <span>Edit</span>
                          </button>
                          {viewMode === 'active' ? (
                          <button 
                            className="students-action-btn archive-btn"
                            title="Archive Parent"
                            onClick={() => handleArchiveParent(parent)}
                            disabled={isSubmitting}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                              <line x1="12" y1="22.08" x2="12" y2="12"></line>
                            </svg>
                            <span>Archive</span>
                          </button>
                          ) : (
                            <button 
                              className="students-action-btn restore-btn"
                              title="Restore Parent"
                              onClick={() => handleRestoreParent(parent)}
                              disabled={isSubmitting}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                              </svg>
                              <span>Restore</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>

      {/* Parent Details Popup */}
      {showDetailsPopup && selectedParent && (
        <div className="popup-overlay" onClick={handleCloseDetails}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', paddingBottom: '2rem' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Parent Details: {selectedParent.parent_name || 'N/A'}
              </h3>
              <button className="close-btn" onClick={handleCloseDetails} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
            
            <div style={{ padding: '1.5rem', backgroundColor: '#F9FAFB' }}>
              {/* All Parents Information */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  All Parents/Guardians ({selectedParentAllParents.length})
                </h4>
                {selectedParentAllParents && selectedParentAllParents.length > 0 ? (
                  <div style={{ display: 'grid', gap: '1.25rem' }}>
                    {selectedParentAllParents.map((parent, parentIndex) => (
                      <div 
                        key={parent.id || parentIndex} 
                        style={{
                          padding: '1.5rem',
                          background: '#FFFFFF',
                          borderRadius: '12px',
                          border: '2px solid #E5E7EB',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#FF8C42';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.15)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                        }}></div>
                        
                        {/* Parent Number Label */}
                        <div style={{
                          marginBottom: '1rem',
                          paddingBottom: '0.75rem',
                          borderBottom: '2px solid #E5E7EB'
                        }}>
                          <h5 style={{
                            color: '#FF8C42',
                            fontSize: '1.1rem',
                            fontWeight: '700',
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            {getParentLabel(parent, parentIndex, selectedParentAllParents)}
                            {parent.is_primary_for_student && (
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#FF8C42',
                                color: 'white',
                                borderRadius: '20px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                marginLeft: 'auto'
                              }}>
                                Primary
                              </span>
                            )}
                          </h5>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Parent Name</span>
                            <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                              {parent.name || 'N/A'}
                            </div>
                          </div>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Relationship</span>
                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                              {parent.relationship || 'N/A'}
                            </div>
                          </div>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                              {parent.email && parent.email !== 'N/A' ? (
                                <a href={`mailto:${parent.email}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                                  {parent.email}
                                </a>
                              ) : 'N/A'}
                            </div>
                          </div>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Phone</span>
                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                              {parent.phone && parent.phone !== 'N/A' ? (
                                <a href={`tel:${parent.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                                  {parent.phone}
                                </a>
                              ) : 'N/A'}
                            </div>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Address</span>
                            <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                              {parent.address || 'N/A'}
                            </div>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Permissions</span>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                              {parent.is_primary_for_student && (
                                <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#EFF6FF', color: '#1E40AF', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500' }}>Primary Contact</span>
                              )}
                              {parent.can_pickup && (
                                <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#E0F2FE', color: '#0369A1', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500' }}>Can Pickup</span>
                              )}
                              {parent.is_emergency_contact && (
                                <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500' }}>Emergency Contact</span>
                              )}
                              {parent.pays_fees && (
                                <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500' }}>Pays Fees</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Parent Documents */}
                          {parent.id && parentDocuments[parent.id] && parentDocuments[parent.id].length > 0 && (
                            <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                              <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>Documents</span>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                {parentDocuments[parent.id].map((doc, docIndex) => (
                                  <div
                                    key={doc.id || docIndex}
                                    style={{
                                      padding: '0.75rem',
                                      background: '#F9FAFB',
                                      borderRadius: '8px',
                                      border: '1px solid #E5E7EB',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}
                                  >
                                    <span style={{ fontSize: '1.2rem' }}>
                                      {doc.document_type === 'Parent National ID' ? '🆔' : '📘'}
                                    </span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1F2937', marginBottom: '0.25rem' }}>
                                        {doc.document_type}
                                      </div>
                                      {doc.file_name && (
                                        <div style={{ fontSize: '0.75rem', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {doc.file_name}
                                        </div>
                                      )}
                                    </div>
                                    {doc.document_url && (
                                      <a
                                        href={doc.document_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          padding: '0.375rem 0.75rem',
                                          background: '#FF8C42',
                                          color: 'white',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: '500',
                                          textDecoration: 'none',
                                          transition: 'all 0.2s',
                                          whiteSpace: 'nowrap'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#FF7A2E';
                                          e.target.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = '#FF8C42';
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                      >
                                        View
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '1.5rem',
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    textAlign: 'center',
                    color: '#6B7280'
                  }}>
                    No parent information available
                  </div>
                )}
              </div>

              {/* Children Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Children ({selectedParent.children_count || 0})
                </h4>
                {selectedParent.children && selectedParent.children.length > 0 ? (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {selectedParent.children.map((child, index) => (
                      <div 
                        key={child.id || index} 
                        style={{
                          padding: '1.25rem',
                          background: '#FFFFFF',
                          borderRadius: '12px',
                          border: '2px solid #E5E7EB',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                        }}></div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: '#EFF6FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem'
                          }}>
                            👨‍🎓
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                              {child.name || 'N/A'}
                            </div>
                            <div style={{ color: '#6B7280', fontSize: '0.9rem' }}>
                              Class: {child.class || 'N/A'} - Section: {child.section || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#6B7280',
                    fontStyle: 'italic',
                    background: '#F9FAFB',
                    borderRadius: '12px',
                    border: '2px dashed #D1D5DB'
                  }}>
                    👨‍👩‍👧‍👦 No children found.
                  </div>
                )}
              </div>

              {/* Emergency Contact Information */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Emergency Contact Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Emergency Contact *</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {emergencyContactInfo.contact || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Emergency Phone *</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {emergencyContactInfo.phone && emergencyContactInfo.phone !== 'N/A' ? (
                          <a href={`tel:${emergencyContactInfo.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {emergencyContactInfo.phone}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Parent Popup */}
      {showEditPopup && editingParent && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in',
            overflowY: 'auto',
            padding: '2rem 0'
          }}
          onClick={handleCloseEditPopup}
        >
          <div 
            className="popup-content" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              maxWidth: '800px', 
              maxHeight: '90vh', 
              overflowY: 'auto', 
              paddingBottom: '2rem',
              width: '90%'
            }}
          >
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem', position: 'sticky', top: 0, zIndex: 1 }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Edit Parent: {editingParent.parent_name || 'N/A'}
              </h3>
              <button 
                className="close-btn" 
                onClick={handleCloseEditPopup} 
                style={{ 
                  color: '#FFFFFF', 
                  fontSize: '1.5rem', 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  padding: '0', 
                  width: '30px', 
                  height: '30px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  position: 'absolute',
                  right: '1.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '2rem', backgroundColor: '#F9FAFB' }}>
              {/* All Parents Information */}
              {editingParentAllParents && editingParentAllParents.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                    All Parents/Guardians ({editingParentAllParents.length})
                  </h4>
                  <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
                    {editingParentAllParents.map((parent, parentIndex) => (
                      <div 
                        key={parent.id || parentIndex} 
                        style={{
                          padding: '1.25rem',
                          background: '#FFFFFF',
                          borderRadius: '12px',
                          border: '2px solid #E5E7EB',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                          position: 'relative'
                        }}
                      >
                        {parent.is_primary_for_student && (
                          <div style={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#FF8C42',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            Primary
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>Name:</span>
                            <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>{parent.name || 'N/A'}</div>
                          </div>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>Relationship:</span>
                            <div style={{ marginTop: '0.25rem' }}>{parent.relationship || 'N/A'}</div>
                          </div>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>Email:</span>
                            <div style={{ marginTop: '0.25rem' }}>{parent.email || 'N/A'}</div>
                          </div>
                          <div>
                            <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>Phone:</span>
                            <div style={{ marginTop: '0.25rem' }}>{parent.phone || 'N/A'}</div>
                          </div>
                        </div>
                        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {parent.is_primary_for_student && <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#EFF6FF', color: '#1E40AF', borderRadius: '4px', fontSize: '0.75rem' }}>Primary</span>}
                          {parent.can_pickup && <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#E0F2FE', color: '#0369A1', borderRadius: '4px', fontSize: '0.75rem' }}>Can Pickup</span>}
                          {parent.is_emergency_contact && <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '4px', fontSize: '0.75rem' }}>Emergency</span>}
                          {parent.pays_fees && <span style={{ padding: '0.2rem 0.5rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '4px', fontSize: '0.75rem' }}>Pays Fees</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ 
                    padding: '1rem', 
                    background: '#FFF4ED', 
                    border: '1px solid #FFD4B3', 
                    borderRadius: '8px', 
                    marginBottom: '1.5rem',
                    fontSize: '0.9rem',
                    color: '#92400E'
                  }}>
                    ℹ️ Note: You are editing the primary parent. To edit all parents, please use the student edit page.
                  </div>
                </div>
              )}
              
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Edit Parent Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                        Parent Name <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={editForm.parentName}
                        onChange={(e) => setEditForm({ ...editForm, parentName: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                        placeholder="Enter parent name"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                        Email <span style={{ color: '#EF4444' }}>*</span>
                      </label>
                      <input
                        type="email"
                        value={editForm.parentEmail}
                        onChange={(e) => setEditForm({ ...editForm, parentEmail: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={editForm.parentPhone}
                        onChange={(e) => setEditForm({ ...editForm, parentPhone: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                        Relationship
                      </label>
                      <select
                        value={editForm.parentRelationship}
                        onChange={(e) => setEditForm({ ...editForm, parentRelationship: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                      >
                        <option value="">Select relationship</option>
                        <option value="Father">Father</option>
                        <option value="Mother">Mother</option>
                        <option value="Guardian">Guardian</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                      Address
                    </label>
                    <textarea
                      value={editForm.parentAddress}
                      onChange={(e) => setEditForm({ ...editForm, parentAddress: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        minHeight: '80px',
                        resize: 'vertical'
                      }}
                      placeholder="Enter address"
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Permissions & Settings
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editForm.parentIsPrimary}
                          onChange={(e) => setEditForm({ ...editForm, parentIsPrimary: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#374151' }}>Primary Contact</span>
                      </label>
                    </div>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editForm.parentCanPickup}
                          onChange={(e) => setEditForm({ ...editForm, parentCanPickup: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#374151' }}>Can Pickup</span>
                      </label>
                    </div>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editForm.parentIsEmergencyContact}
                          onChange={(e) => setEditForm({ ...editForm, parentIsEmergencyContact: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#374151' }}>Emergency Contact</span>
                      </label>
                    </div>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={editForm.parentPaysFees}
                          onChange={(e) => setEditForm({ ...editForm, parentPaysFees: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem', color: '#374151' }}>Pays Fees</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
                <button
                  onClick={handleCloseEditPopup}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    border: '2px solid #E5E7EB',
                    background: '#FFFFFF',
                    color: '#6B7280',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: isSubmitting ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveParent}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 2rem',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #FF8C42 0%, #FF7A2E 100%)',
                    color: '#FFFFFF',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: isSubmitting ? 0.5 : 1,
                    boxShadow: '0 4px 12px rgba(255, 140, 66, 0.4)'
                  }}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Popup */}
      {showArchiveConfirmPopup && parentToArchive && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelArchiveParent}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Archive Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>📦</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Archive Parent?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to archive <strong>{parentToArchive.parent_name}</strong>?
              <br /><br />
              This will archive all students associated with this parent ({parentToArchive.children_count || 0} {parentToArchive.children_count === 1 ? 'child' : 'children'}). The parent and their children will be marked as inactive.
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelArchiveParent}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: '#FFFFFF',
                  color: '#6B7280',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = '#F9FAFB';
                    e.target.style.borderColor = '#D1D5DB';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FFFFFF';
                  e.target.style.borderColor = '#E5E7EB';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmArchiveParent}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1,
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
                }}
              >
                {isSubmitting ? 'Archiving...' : 'Archive Parent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Success Popup */}
      {showArchiveSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowArchiveSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Parent Archived Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong>{archivedParentName}</strong> and all associated students have been archived successfully.
              <br />
              They are now marked as inactive.
            </p>

            {/* Button */}
            <button
              onClick={() => setShowArchiveSuccessPopup(false)}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Restore Confirmation Popup */}
      {showRestoreConfirmPopup && parentToRestore && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelRestoreParent}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Restore Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>♻️</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Restore Parent?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to restore <strong>{parentToRestore.parent_name}</strong>?
              <br /><br />
              This will restore all students associated with this parent ({parentToRestore.children_count || 0} {parentToRestore.children_count === 1 ? 'child' : 'children'}). The parent and their children will be marked as active.
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelRestoreParent}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: '#FFFFFF',
                  color: '#6B7280',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = '#F9FAFB';
                    e.target.style.borderColor = '#D1D5DB';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FFFFFF';
                  e.target.style.borderColor = '#E5E7EB';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRestoreParent}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1,
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }}
              >
                {isSubmitting ? 'Restoring...' : 'Restore Parent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Success Popup */}
      {showRestoreSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowRestoreSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Parent Restored Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong>{restoredParentName}</strong> and all associated students have been restored successfully.
              <br />
              They are now marked as active.
            </p>

            {/* Button */}
            <button
              onClick={() => setShowRestoreSuccessPopup(false)}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Update Success Popup */}
      {showUpdateSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowUpdateSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 122, 46, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 122, 46, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF8C42 0%, #FF7A2E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(255, 140, 66, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Parent Updated Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong>{updatedParentName}</strong> has been updated successfully.
              <br />
              All associated students have been updated with the new parent information.
            </p>

            {/* Button */}
            <button
              onClick={() => setShowUpdateSuccessPopup(false)}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF7A2E 100%)',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(255, 140, 66, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 66, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 66, 0.4)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default Parents;

